import { Article } from '../types';

export const firstLoginArticle: Article = {
  id: 'first-login-setup',
  title: 'First Login & Setup',
  slug: 'getting-started-guide',
  description: 'Complete guide to logging in for the first time and setting up your CRM account with all essential configurations',
  category: 'getting-started',
  tags: ['login', 'setup', 'onboarding', 'account', 'configuration', 'getting-started'],
  lastUpdated: new Date('2024-01-25'),
  readTime: 15,
  content: `
    <h2>Welcome to Your CRM</h2>
    <p>This comprehensive guide will walk you through your first login and help you set up your CRM account properly. By the end of this guide, you'll have a fully configured system ready for your business operations.</p>

    <div class="bg-blue-50 p-4 rounded-lg mt-4 mb-6">
      <strong>Before You Begin:</strong> Make sure you have received your welcome email with login credentials. You'll need your email address and temporary password to get started.
    </div>

    <h2 id="accessing-crm">Step 1: Accessing Your CRM</h2>
    
    <h3>Getting to the Login Page</h3>
    <ol>
      <li>Open your web browser (Chrome, Firefox, Safari, or Edge recommended)</li>
      <li>Navigate to the CRM URL: <code>https://app.thedefinedcrm.com</code></li>
      <li>You'll see the login page with the Defined CRM logo</li>
      <li>Bookmark this page for easy access in the future</li>
    </ol>

    <h3>System Requirements</h3>
    <ul>
      <li><strong>Desktop:</strong> Any modern browser updated within the last year</li>
      <li><strong>Mobile:</strong> iOS 14+ or Android 10+ with Chrome or Safari</li>
      <li><strong>Internet:</strong> Stable broadband connection (minimum 5 Mbps recommended)</li>
      <li><strong>Screen Resolution:</strong> Minimum 1024x768, optimal 1920x1080 or higher</li>
    </ul>

    <h2 id="first-login">Step 2: Your First Login</h2>
    
    <h3>Using Your Temporary Credentials</h3>
    <ol>
      <li>Enter the email address provided in your welcome email</li>
      <li>Enter the temporary password (case-sensitive)</li>
      <li>Click the "Sign In" button</li>
      <li>If you see an error, double-check for typos and ensure Caps Lock is off</li>
    </ol>

    <div class="bg-yellow-50 p-4 rounded-lg mt-4 mb-6">
      <strong>Security Note:</strong> Your temporary password expires after 72 hours. If it has expired, click "Forgot Password?" to receive a new one.
    </div>

    <h3>Creating Your Secure Password</h3>
    <p>After logging in with temporary credentials, you'll be prompted to create a new password:</p>
    <ul>
      <li><strong>Minimum 8 characters</strong> (12+ recommended)</li>
      <li><strong>At least one uppercase letter</strong> (A-Z)</li>
      <li><strong>At least one lowercase letter</strong> (a-z)</li>
      <li><strong>At least one number</strong> (0-9)</li>
      <li><strong>At least one special character</strong> (!@#$%^&*)</li>
      <li><strong>Cannot match</strong> your last 5 passwords</li>
    </ul>

    <p><strong>Password Tips:</strong></p>
    <ul>
      <li>Use a passphrase: "MyClean!ngBus1ness2024"</li>
      <li>Avoid personal information (birthdays, names, addresses)</li>
      <li>Consider using a password manager</li>
      <li>Never share your password with anyone</li>
    </ul>

    <h2 id="account-setup">Step 3: Account Profile Setup</h2>
    
    <h3>Personal Information</h3>
    <p>Complete your user profile with accurate information:</p>
    <ol>
      <li><strong>Full Name:</strong> Enter your first and last name</li>
      <li><strong>Job Title:</strong> Your role in the company (e.g., Owner, Manager, Technician)</li>
      <li><strong>Phone Number:</strong> Direct line for system notifications</li>
      <li><strong>Mobile Number:</strong> For SMS alerts (optional but recommended)</li>
    </ol>

    <h3>Communication Preferences</h3>
    <p>Configure how you want to receive system notifications:</p>
    <ul>
      <li><strong>Email Notifications:</strong>
        <ul>
          <li>New appointments assigned to you</li>
          <li>Customer messages and inquiries</li>
          <li>Payment confirmations</li>
          <li>System alerts and updates</li>
        </ul>
      </li>
      <li><strong>SMS Notifications:</strong>
        <ul>
          <li>Urgent appointment changes</li>
          <li>Customer cancellations</li>
          <li>Emergency system alerts</li>
        </ul>
      </li>
    </ul>

    <h2 id="company-setup">Step 4: Company Information Setup</h2>
    
    <h3>Basic Company Details</h3>
    <p>Navigate to Settings > Company Profile to enter:</p>
    <ol>
      <li><strong>Company Name:</strong> Your official business name</li>
      <li><strong>DBA (Doing Business As):</strong> If different from legal name</li>
      <li><strong>Business Type:</strong> Sole Proprietorship, LLC, Corporation, etc.</li>
      <li><strong>Tax ID/EIN:</strong> For invoice and tax purposes</li>
      <li><strong>Business License Number:</strong> If applicable in your area</li>
      <li><strong>Insurance Information:</strong> Policy numbers and expiration dates</li>
    </ol>

    <h3>Contact Information</h3>
    <ul>
      <li><strong>Main Phone:</strong> Primary business phone number</li>
      <li><strong>Support Email:</strong> Customer service email address</li>
      <li><strong>Billing Email:</strong> For invoice-related communications</li>
      <li><strong>Website:</strong> Your company website URL</li>
      <li><strong>Social Media:</strong> Links to Facebook, Instagram, LinkedIn profiles</li>
    </ul>

    <h3>Business Address</h3>
    <ol>
      <li><strong>Street Address:</strong> Physical business location</li>
      <li><strong>City, State, ZIP:</strong> Complete address details</li>
      <li><strong>Service Areas:</strong> ZIP codes or regions you serve</li>
      <li><strong>Billing Address:</strong> If different from physical address</li>
    </ol>

    <h2 id="invoice-setup">Step 5: Invoice Settings</h2>
    
    <h3>Invoice Templates</h3>
    <p>Configure your invoice settings:</p>
    <ul>
      <li><strong>Invoice Numbering:</strong> Set starting invoice number and format</li>
      <li><strong>Payment Terms:</strong> Net 15, Net 30, Due on receipt</li>
      <li><strong>Late Fees:</strong> Configure late payment penalties if applicable</li>
      <li><strong>Invoice Footer:</strong> Add terms, conditions, and payment instructions</li>
      <li><strong>Thank You Message:</strong> Personalized message for customers</li>
    </ul>

    <h2 id="business-hours">Step 6: Business Hours & Calendar</h2>
    
    <h3>Operating Hours</h3>
    <p>Define your standard business hours:</p>
    <ol>
      <li>Navigate to Settings > Business Hours</li>
      <li>Set hours for each day of the week:
        <ul>
          <li>Monday - Friday: 8:00 AM - 6:00 PM</li>
          <li>Saturday: 9:00 AM - 3:00 PM</li>
          <li>Sunday: Closed</li>
        </ul>
      </li>
      <li>Add any seasonal variations</li>
      <li>Configure holiday schedule</li>
    </ol>

    <h3>Time Zone Settings</h3>
    <ul>
      <li>Select your primary time zone</li>
      <li>Enable automatic daylight saving adjustments</li>
      <li>Set default appointment duration (30, 60, 90 minutes)</li>
      <li>Configure buffer time between appointments</li>
    </ul>

    <h2 id="payment-setup">Step 7: Payment Configuration</h2>
    
    <h3>Payment Methods</h3>
    <p>Enable the payment options you accept:</p>
    <ul>
      <li><strong>Cash:</strong> Track cash payments and deposits</li>
      <li><strong>Check:</strong> Record check numbers and clearing dates</li>
      <li><strong>Credit/Debit Cards:</strong> Connect Stripe or Square account</li>
      <li><strong>ACH/Bank Transfer:</strong> For commercial accounts</li>
      <li><strong>Online Payments:</strong> Enable customer portal payments</li>
    </ul>

    <h3>Tax Settings</h3>
    <ol>
      <li>Set default tax rate for your area</li>
      <li>Configure tax-exempt options</li>
      <li>Add multiple tax rates if serving different regions</li>
      <li>Enable automatic tax calculations</li>
    </ol>

    <h2 id="team-setup">Step 8: Team Member Setup</h2>
    
    <h3>Adding Your First Employee</h3>
    <p>If you have team members, add them now:</p>
    <ol>
      <li>Go to Settings > Team Members</li>
      <li>Click "Add New Employee"</li>
      <li>Enter employee information:
        <ul>
          <li>Name and contact details</li>
          <li>Email address (for login)</li>
          <li>Role (Admin, Manager, Technician, Office Staff)</li>
          <li>Hourly rate or salary (for reporting)</li>
        </ul>
      </li>
      <li>Set permissions based on role</li>
      <li>Send invitation email</li>
    </ol>

    <h3>Permission Levels</h3>
    <table>
      <thead>
        <tr>
          <th>Role</th>
          <th>Access Level</th>
          <th>Typical Permissions</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td><strong>Admin</strong></td>
          <td>Full Access</td>
          <td>All features, settings, reports, financial data</td>
        </tr>
        <tr>
          <td><strong>Manager</strong></td>
          <td>Management Access</td>
          <td>Scheduling, customers, invoicing, limited reports</td>
        </tr>
        <tr>
          <td><strong>Technician</strong></td>
          <td>Field Access</td>
          <td>View schedule, update job status, add notes</td>
        </tr>
        <tr>
          <td><strong>Office Staff</strong></td>
          <td>Administrative Access</td>
          <td>Customer service, scheduling, basic invoicing</td>
        </tr>
      </tbody>
    </table>

    <h2 id="services-setup">Step 9: Services & Pricing Setup</h2>
    
    <h3>Creating Your Service List</h3>
    <p>Add the services you offer:</p>
    <ol>
      <li>Navigate to Settings > Services</li>
      <li>Click "Add New Service"</li>
      <li>For each service, enter:
        <ul>
          <li><strong>Service Name:</strong> Clear, descriptive name</li>
          <li><strong>Category:</strong> Group similar services</li>
          <li><strong>Description:</strong> What's included</li>
          <li><strong>Duration:</strong> Typical time required</li>
          <li><strong>Base Price:</strong> Standard rate</li>
          <li><strong>Unit Type:</strong> Per hour, per visit, per square foot</li>
        </ul>
      </li>
    </ol>

    <h3>Common Service Examples</h3>
    <ul>
      <li><strong>Residential Cleaning:</strong>
        <ul>
          <li>Standard Clean - $100-150/visit</li>
          <li>Deep Clean - $200-300/visit</li>
          <li>Move-in/Move-out - $250-400/visit</li>
        </ul>
      </li>
      <li><strong>Commercial Cleaning:</strong>
        <ul>
          <li>Office Cleaning - $0.05-0.20/sq ft</li>
          <li>Retail Space - $0.10-0.25/sq ft</li>
          <li>Medical Facility - $0.15-0.35/sq ft</li>
        </ul>
      </li>
      <li><strong>Specialty Services:</strong>
        <ul>
          <li>Window Cleaning - $5-15/window</li>
          <li>Carpet Cleaning - $0.20-0.40/sq ft</li>
          <li>Pressure Washing - $0.15-0.30/sq ft</li>
        </ul>
      </li>
    </ul>

    <h2 id="integration">Step 10: Third-Party Integrations</h2>
    
    <h3>Email Integration</h3>
    <p>Connect your email for seamless communication:</p>
    <ol>
      <li>Go to Settings > Integrations</li>
      <li>Select your email provider (Gmail, Outlook, etc.)</li>
      <li>Authorize the connection</li>
      <li>Configure sync settings:
        <ul>
          <li>Automatic email logging</li>
          <li>Customer communication tracking</li>
          <li>Email templates sync</li>
        </ul>
      </li>
    </ol>

    <h3>Google Calendar Sync</h3>
    <p>Connect your Google Calendar for seamless scheduling:</p>
    <ol>
      <li>Go to Settings > Integrations > Google Calendar</li>
      <li>Click "Connect Google Calendar"</li>
      <li>Sign in to your Google account</li>
      <li>Grant permission for calendar access</li>
      <li>Select which calendar to sync with</li>
      <li>Choose sync preferences:
        <ul>
          <li>Two-way sync (recommended)</li>
          <li>Sync frequency (real-time or every 15 minutes)</li>
          <li>Include customer names in calendar events</li>
        </ul>
      </li>
    </ol>

    <h3>Payment Processing</h3>
    <p>Set up online payment processing:</p>
    <ol>
      <li>Choose Stripe or Square</li>
      <li>Create or connect existing account</li>
      <li>Complete verification process</li>
      <li>Configure payment settings:
        <ul>
          <li>Accept cards on file</li>
          <li>Enable autopay options</li>
          <li>Set up deposit schedules</li>
        </ul>
      </li>
    </ol>

    <h2 id="mobile-setup">Step 11: Mobile App Setup</h2>
    
    <h3>Downloading the Mobile App</h3>
    <ol>
      <li><strong>iOS Users:</strong>
        <ul>
          <li>Open App Store</li>
          <li>Search for "Defined Cleaning CRM"</li>
          <li>Download and install</li>
        </ul>
      </li>
      <li><strong>Android Users:</strong>
        <ul>
          <li>Open Google Play Store</li>
          <li>Search for "Defined Cleaning CRM"</li>
          <li>Download and install</li>
        </ul>
      </li>
    </ol>

    <h3>Mobile Login</h3>
    <p>Sign in to the mobile app:</p>
    <ol>
      <li>Open the app</li>
      <li>Enter your CRM URL or company code</li>
      <li>Use the same email and password as web version</li>
      <li>Enable biometric login (fingerprint/face ID) for convenience</li>
      <li>Allow notifications for real-time updates</li>
    </ol>

    <h2 id="data-import">Step 12: Import Existing Data</h2>
    
    <h3>Customer Import</h3>
    <p>If you have existing customer data:</p>
    <ol>
      <li>Go to Customers > Import</li>
      <li>Download the CSV template</li>
      <li>Format your data to match template columns:
        <ul>
          <li>First Name, Last Name</li>
          <li>Email, Phone</li>
          <li>Address fields</li>
          <li>Customer type and tags</li>
        </ul>
      </li>
      <li>Upload your CSV file</li>
      <li>Review and map fields</li>
      <li>Confirm import</li>
    </ol>

    <div class="bg-green-50 p-4 rounded-lg mt-4 mb-6">
      <strong>Pro Tip:</strong> Start with a small test import (5-10 customers) to ensure your data formats correctly before importing your entire database.
    </div>

    <h2 id="training">Step 13: Training Resources</h2>
    
    <h3>Available Training Options</h3>
    <ul>
      <li><strong>Video Tutorials:</strong> 
        <ul>
          <li>Getting Started Series (5 videos, 30 minutes total)</li>
          <li>Feature deep-dives</li>
          <li>Best practices guides</li>
        </ul>
      </li>
      <li><strong>Live Webinars:</strong>
        <ul>
          <li>Weekly new user orientation</li>
          <li>Monthly feature updates</li>
          <li>Q&A sessions</li>
        </ul>
      </li>
      <li><strong>Documentation:</strong>
        <ul>
          <li>This help center</li>
          <li>PDF user manual</li>
          <li>Quick reference guides</li>
        </ul>
      </li>
    </ul>

    <h3>Practice Mode</h3>
    <p>Use the training environment to practice:</p>
    <ol>
      <li>Access via Settings > Training Mode</li>
      <li>Sample data is pre-loaded</li>
      <li>Try all features without affecting real data</li>
      <li>Reset anytime to start fresh</li>
    </ol>

    <h2 id="checklist">Setup Completion Checklist</h2>
    
    <p>Ensure you've completed all essential setup steps:</p>
    <ul style="list-style-type: none;">
      <li>☐ Changed password from temporary</li>
      <li>☐ Completed user profile</li>
      <li>☐ Added company information</li>
      <li>☐ Configured invoice settings</li>
      <li>☐ Set business hours</li>
      <li>☐ Configured payment methods</li>
      <li>☐ Added team members (if applicable)</li>
      <li>☐ Created service list with pricing</li>
      <li>☐ Connected email account</li>
      <li>☐ Synced Google Calendar</li>
      <li>☐ Set up payment processing</li>
      <li>☐ Downloaded mobile app</li>
      <li>☐ Imported customer data (if applicable)</li>
      <li>☐ Completed basic training</li>
    </ul>

    <h2 id="next-steps">What's Next?</h2>
    
    <p>Now that your account is set up, here are recommended next steps:</p>
    <ol>
      <li><strong>Add Your First Customer:</strong> Practice by adding a test customer</li>
      <li><strong>Create a Test Appointment:</strong> Schedule a sample appointment</li>
      <li><strong>Generate a Test Invoice:</strong> Create and send a test invoice</li>
      <li><strong>Explore Reports:</strong> View available reports and dashboards</li>
      <li><strong>Customize Workflows:</strong> Adjust settings to match your process</li>
    </ol>

    <div class="bg-blue-50 p-4 rounded-lg mt-6">
      <strong>Need Help?</strong> Our support team is available Monday-Friday, 8 AM - 6 PM PST. Contact us at support@definedcleaning.com or call (123) 456-7890. We're here to ensure your success!
    </div>

    <h2 id="troubleshooting">Common Setup Issues</h2>
    
    <h3>Login Problems</h3>
    <ul>
      <li><strong>Forgot Password:</strong> Use the "Forgot Password?" link on login page</li>
      <li><strong>Account Locked:</strong> After 5 failed attempts, wait 15 minutes or contact support</li>
      <li><strong>Browser Issues:</strong> Clear cache and cookies, try incognito mode</li>
    </ul>

    <h3>Email Verification</h3>
    <ul>
      <li>Check spam/junk folder for verification email</li>
      <li>Add noreply@definedcleaning.com to contacts</li>
      <li>Request new verification email from profile settings</li>
    </ul>

    <h3>Mobile App Issues</h3>
    <ul>
      <li><strong>Can't Find Company:</strong> Enter exact CRM URL provided</li>
      <li><strong>Login Failed:</strong> Ensure using same credentials as web</li>
      <li><strong>Sync Problems:</strong> Check internet connection, force refresh</li>
    </ul>
  `,
  tableOfContents: [
    { id: 'accessing-crm', title: 'Step 1: Accessing Your CRM' },
    { id: 'first-login', title: 'Step 2: Your First Login' },
    { id: 'account-setup', title: 'Step 3: Account Profile Setup' },
    { id: 'company-setup', title: 'Step 4: Company Information' },
    { id: 'invoice-setup', title: 'Step 5: Invoice Settings' },
    { id: 'business-hours', title: 'Step 6: Business Hours & Calendar' },
    { id: 'payment-setup', title: 'Step 7: Payment Configuration' },
    { id: 'team-setup', title: 'Step 8: Team Member Setup' },
    { id: 'services-setup', title: 'Step 9: Services & Pricing' },
    { id: 'integration', title: 'Step 10: Third-Party Integrations' },
    { id: 'mobile-setup', title: 'Step 11: Mobile App Setup' },
    { id: 'data-import', title: 'Step 12: Import Existing Data' },
    { id: 'training', title: 'Step 13: Training Resources' },
    { id: 'checklist', title: 'Setup Completion Checklist' },
    { id: 'next-steps', title: 'What\'s Next?' },
    { id: 'troubleshooting', title: 'Common Setup Issues' }
  ],
  relatedArticles: [
    'company-profile-setup',
    'user-settings',
    'adding-team-members',
    'importing-customers-csv',
    'service-configuration'
  ]
};